import { TokenValidationResult } from '../types/authTypes';

/**
 * Utilitários para manipulação e validação de tokens JWT
 */
export class TokenUtils {
  /**
   * Decodifica um token JWT sem verificar a assinatura
   * @param token - Token JWT
   * @returns Payload decodificado ou null se inválido
   */
  static decodeJWT(token: string): any | null {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = parts[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('[TokenUtils] Erro ao decodificar JWT:', error);
      return null;
    }
  }

  /**
   * Valida se um token JWT é válido e não expirou
   * @param token - Token JWT
   * @returns Resultado da validação
   */
  static validateToken(token: string): TokenValidationResult {
    try {
      if (!token) {
        return {
          isValid: false,
          isExpired: true,
        };
      }

      const payload = this.decodeJWT(token);
      if (!payload) {
        return {
          isValid: false,
          isExpired: true,
        };
      }

      const now = Math.floor(Date.now() / 1000);
      const expiresAt = payload.exp || payload.expires_at;

      if (!expiresAt) {
        return {
          isValid: true,
          isExpired: false,
        };
      }

      const isExpired = now >= expiresAt;
      const timeUntilExpiry = expiresAt - now;

      return {
        isValid: !isExpired,
        expiresAt: expiresAt * 1000, // Converter para milissegundos
        isExpired,
        timeUntilExpiry: timeUntilExpiry > 0 ? timeUntilExpiry : 0,
      };
    } catch (error) {
      console.error('[TokenUtils] Erro ao validar token:', error);
      return {
        isValid: false,
        isExpired: true,
      };
    }
  }

  /**
   * Verifica se um token está próximo de expirar
   * @param token - Token JWT
   * @param thresholdSeconds - Segundos antes da expiração para considerar "próximo de expirar"
   * @returns true se estiver próximo de expirar
   */
  static isTokenNearExpiry(token: string, thresholdSeconds: number = 300): boolean {
    const validation = this.validateToken(token);

    if (!validation.isValid || !validation.timeUntilExpiry) {
      return true;
    }

    return validation.timeUntilExpiry <= thresholdSeconds;
  }

  /**
   * Calcula o tempo restante de um token em formato legível
   * @param token - Token JWT
   * @returns String formatada com tempo restante
   */
  static getTokenTimeRemaining(token: string): string {
    const validation = this.validateToken(token);

    if (!validation.isValid || !validation.timeUntilExpiry) {
      return 'Expirado';
    }

    const hours = Math.floor(validation.timeUntilExpiry / 3600);
    const minutes = Math.floor((validation.timeUntilExpiry % 3600) / 60);
    const seconds = validation.timeUntilExpiry % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Extrai informações do usuário de um token JWT
   * @param token - Token JWT
   * @returns Informações do usuário ou null
   */
  static extractUserInfo(token: string): {
    userId?: string;
    email?: string;
    roles?: string[];
    permissions?: string[];
  } | null {
    try {
      const payload = this.decodeJWT(token);
      if (!payload) {
        return null;
      }

      return {
        userId: payload.sub || payload.user_id || payload.userId,
        email: payload.email || payload.email_address,
        roles: payload.roles || payload.role || [],
        permissions: payload.permissions || payload.permission || [],
      };
    } catch (error) {
      console.error('[TokenUtils] Erro ao extrair informações do usuário:', error);
      return null;
    }
  }

  /**
   * Verifica se um token tem uma role específica
   * @param token - Token JWT
   * @param role - Role a ser verificada
   * @returns true se o token tem a role
   */
  static hasRole(token: string, role: string): boolean {
    const userInfo = this.extractUserInfo(token);
    if (!userInfo || !userInfo.roles) {
      return false;
    }

    return userInfo.roles.includes(role);
  }

  /**
   * Verifica se um token tem uma permissão específica
   * @param token - Token JWT
   * @param permission - Permissão a ser verificada
   * @returns true se o token tem a permissão
   */
  static hasPermission(token: string, permission: string): boolean {
    const userInfo = this.extractUserInfo(token);
    if (!userInfo || !userInfo.permissions) {
      return false;
    }

    return userInfo.permissions.includes(permission);
  }

  /**
   * Gera um timestamp de expiração baseado em um número de segundos
   * @param secondsFromNow - Segundos a partir de agora
   * @returns Timestamp em milissegundos
   */
  static generateExpiryTimestamp(secondsFromNow: number): number {
    return Date.now() + secondsFromNow * 1000;
  }

  /**
   * Verifica se um timestamp já expirou
   * @param timestamp - Timestamp em milissegundos
   * @returns true se expirou
   */
  static isTimestampExpired(timestamp: number): boolean {
    return Date.now() >= timestamp;
  }
}
